import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Button, TextField } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Layout from '../components/Layout';
import { sendEmail } from '../utils/emailService';

const features = [
  {
    icon: <SupportAgentIcon sx={{ fontSize: 26 }} />,
    title: 'Customer Ticket Portal',
    description: 'Customers submit product issues directly through a self-service portal. No more email chains or WhatsApp messages — every request is logged, tracked, and visible.',
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 26 }} />,
    title: 'Auto Engineer Assignment',
    description: 'Tickets are automatically routed to the right engineer based on product type, issue category, or workload. No manual dispatching, no dropped balls.',
  },
  {
    icon: <NotificationsActiveIcon sx={{ fontSize: 26 }} />,
    title: 'Real-Time Notifications',
    description: 'Customers and engineers get instant updates at every stage — ticket opened, assigned, in progress, resolved. Everyone stays informed without chasing.',
  },
  {
    icon: <ChatIcon sx={{ fontSize: 26 }} />,
    title: 'Live Chat on Tickets',
    description: 'Engineers and customers communicate directly on the ticket thread. Context stays in one place — no switching between apps, no lost messages.',
  },
  {
    icon: <CloudUploadIcon sx={{ fontSize: 26 }} />,
    title: 'File & Evidence Uploads',
    description: 'Customers attach photos, videos, or documents to their ticket. Engineers upload reports, manuals, or resolution evidence. Everything stored against the ticket.',
  },
  {
    icon: <HistoryIcon sx={{ fontSize: 26 }} />,
    title: 'Full Audit Log',
    description: 'Every action on every ticket is logged — who changed what, when, and why. Complete traceability for compliance, disputes, and quality reviews.',
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 26 }} />,
    title: 'Analytics & Reporting',
    description: 'Track resolution times, ticket volumes, engineer performance, and recurring issues. Data that helps you improve your product and your support operation.',
  },
  {
    icon: <AdminPanelSettingsIcon sx={{ fontSize: 26 }} />,
    title: 'Admin Dashboard',
    description: 'Full visibility across all open, in-progress, and resolved tickets. Reassign, escalate, or close tickets. Monitor your team\'s workload in real time.',
  },
];

const painPoints = [
  { before: 'Customers email support@yourcompany.com and wait days for a response', after: 'Ticket created instantly, engineer assigned automatically within minutes' },
  { before: 'Issue history scattered across email threads and WhatsApp groups', after: 'Complete ticket history, chat, and files in one searchable place' },
  { before: 'No visibility into how long issues take to resolve', after: 'Live analytics on resolution time, volume, and engineer performance' },
  { before: 'Customers have no idea what\'s happening with their issue', after: 'Real-time status updates and direct chat with the assigned engineer' },
];

const techStack = ['React 18', 'Material-UI 5', 'Node.js', 'Express.js', 'PostgreSQL', 'AWS S3', 'WebSockets', 'JWT Auth', 'Docker'];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#F9FAFB', borderRadius: '10px',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#6366F1', borderWidth: '1px' },
  },
  '& .MuiInputLabel-root': { color: '#9CA3AF' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#6366F1' },
  '& .MuiOutlinedInput-input': { color: '#0F1117' },
};

const GearDeskPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const result = await sendEmail(formData, 'GearDesk Demo Request');
    setSending(false);
    if (result.success) {
      alert("Thanks! We'll reach out to schedule your demo.");
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } else {
      alert('Failed to send. Please email admin@infynihub.com directly.');
    }
  };

  const set = (f) => (e) => setFormData({ ...formData, [f]: e.target.value });

  return (
    <Layout>
      <Box sx={{ bgcolor: '#FFFFFF', pt: { xs: '140px', md: '150px' } }}>

        {/* Hero */}
        <Box sx={{ py: { xs: 6, md: 10 }, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: 0, right: 0, width: { xs: 250, md: 500 }, height: { xs: 250, md: 500 }, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '100px', px: 2, py: 0.6, mb: 3 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366F1' }} />
              <Typography sx={{ color: '#4F46E5', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Product Support Ticketing</Typography>
            </Box>
            <Typography variant="h1" sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '2.4rem', sm: '3rem', md: '3.8rem' }, lineHeight: 1.08, letterSpacing: '-0.03em', mb: 2 }}>
              GearDesk
            </Typography>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: { xs: '1rem', md: '1.2rem' }, mb: 3 }}>
              End-to-End Customer Support Ticketing System
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: { xs: '0.95rem', md: '1.05rem' }, lineHeight: 1.85, maxWidth: 620, mb: 3 }}>
              Stop losing customer issues in email threads and WhatsApp groups. GearDesk gives your customers a proper portal to raise tickets, auto-assigns the right engineer, and manages the entire support cycle — with real-time chat, notifications, file uploads, and analytics — all in one place.
            </Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '8px', px: 2, py: 1, mb: 5 }}>
              <Typography sx={{ color: '#C2410C', fontSize: '0.82rem', fontWeight: 600 }}>
                Built for manufacturers who support their own products — not a generic helpdesk tool.
              </Typography>
            </Box>
            <Box sx={{ display: 'block' }}>
              <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
                onClick={() => document.getElementById('geardesk-contact').scrollIntoView({ behavior: 'smooth' })}
                sx={{ bgcolor: '#6366F1', color: 'white', px: { xs: 3, md: 4 }, py: 1.6, fontWeight: 700, borderRadius: '8px', textTransform: 'none', boxShadow: '0 4px 20px rgba(99,102,241,0.3)', '&:hover': { bgcolor: '#5254CC', transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
                Request a Demo
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Before / After */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#F8F9FF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>The Problem</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: { xs: 4, md: 6 } }}>
              Sound familiar?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {painPoints.map((p, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, p: 2.5, borderRadius: '12px', bgcolor: '#FEF2F2', border: '1px solid #FECACA', alignItems: 'flex-start' }}>
                    <Typography sx={{ color: '#DC2626', fontWeight: 800, fontSize: '1rem', flexShrink: 0 }}>✕</Typography>
                    <Typography sx={{ color: '#7F1D1D', fontSize: '0.88rem', lineHeight: 1.7 }}>{p.before}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, p: 2.5, borderRadius: '12px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', alignItems: 'flex-start' }}>
                    <Typography sx={{ color: '#16A34A', fontWeight: 800, fontSize: '1rem', flexShrink: 0 }}>✓</Typography>
                    <Typography sx={{ color: '#14532D', fontSize: '0.88rem', lineHeight: 1.7 }}>{p.after}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* Features */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Features</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: { xs: 4, md: 6 } }}>Everything in One Ticket</Typography>
            <Grid container spacing={2.5}>
              {features.map((f, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Box sx={{ p: 3, borderRadius: '14px', bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%' }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366F1', mb: 2 }}>
                      {f.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#0F1117', mb: 0.75, fontSize: '0.95rem' }}>{f.title}</Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.83rem', lineHeight: 1.7 }}>{f.description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Tech Stack */}
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#F8F9FF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Built With</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: 4 }}>Technology Stack</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {techStack.map((t) => (
                <Box key={t} sx={{ px: 2.5, py: 1, borderRadius: '8px', bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', color: '#374151', fontSize: '0.88rem', fontWeight: 500 }}>{t}</Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* Deployment Options */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Deployment</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: 2 }}>Made to fit your business.</Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '0.95rem', lineHeight: 1.8, mb: { xs: 4, md: 6 }, maxWidth: 560 }}>
              GearDesk isn't one-size-fits-all. Choose how you want it deployed, branded, and hosted.
            </Typography>
            <Grid container spacing={2.5}>
              {[
                {
                  icon: '🎨',
                  title: 'Custom Branding',
                  body: 'Your logo, your colours, your domain name on the login screen and throughout the app. Customers see your brand — not ours.',
                  tags: ['White-label', 'Logo & colours', 'Custom email templates'],
                },
                {
                  icon: '🌐',
                  title: 'Custom Domain',
                  body: 'Run GearDesk on your own domain — e.g. support.yourcompany.com. Full SSL included. Looks and feels like a product you built yourself.',
                  tags: ['support.yourcompany.com', 'SSL included', 'DNS setup support'],
                },
                {
                  icon: '🖥️',
                  title: 'Self-Hosting',
                  body: 'Want full control over your data and infrastructure? We deploy GearDesk on your own servers or cloud account. Your data never leaves your environment.',
                  tags: ['Your servers', 'Your cloud account', 'Full data ownership'],
                },
              ].map((opt) => (
                <Grid item xs={12} md={4} key={opt.title}>
                  <Box sx={{ p: 3.5, borderRadius: '16px', bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: '2rem', mb: 2 }}>{opt.icon}</Typography>
                    <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '1.05rem', mb: 1.5 }}>{opt.title}</Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.88rem', lineHeight: 1.75, mb: 2.5, flexGrow: 1 }}>{opt.body}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {opt.tags.map(t => (
                        <Box key={t} sx={{ px: 1.5, py: 0.4, borderRadius: '100px', bgcolor: '#EEF2FF', border: '1px solid #C7D2FE' }}>
                          <Typography sx={{ color: '#4F46E5', fontSize: '0.72rem', fontWeight: 600 }}>{t}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Contact */}
        <Box id="geardesk-contact" sx={{ py: { xs: 6, md: 10 }, bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="md" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Get Started</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: 1.5 }}>Request a GearDesk Demo</Typography>
            <Typography sx={{ color: '#6B7280', mb: 4, lineHeight: 1.8 }}>Tell us how you currently handle customer support and we'll show you exactly how GearDesk replaces it.</Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', borderRadius: '20px', p: { xs: 3, md: 4 } }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Name" required value={formData.name} onChange={set('name')} sx={fieldSx} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Email" type="email" required value={formData.email} onChange={set('email')} sx={fieldSx} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Company" value={formData.company} onChange={set('company')} sx={fieldSx} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={formData.phone} onChange={set('phone')} sx={fieldSx} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="How do you currently handle customer support issues?" multiline rows={4} required value={formData.message} onChange={set('message')} sx={fieldSx} /></Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" size="large" disabled={sending} endIcon={<ArrowForwardIcon />} fullWidth
                    sx={{ bgcolor: '#6366F1', py: 1.6, fontWeight: 700, fontSize: '1rem', borderRadius: '10px', textTransform: 'none', boxShadow: '0 4px 20px rgba(99,102,241,0.3)', '&:hover': { bgcolor: '#5254CC' }, transition: 'all 0.25s ease' }}>
                    {sending ? 'Sending...' : 'Request Demo'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
    </Layout>
  );
};

export default GearDeskPage;
